import type { Birthday } from '../entities/Birthday';
import { CalendarDate } from '../value-objects/CalendarDate';

/**
 * Qué cumpleaños caen cuándo.
 *
 * Todo sale de una sola regla, `celebrationDay`: el día en que se celebra un
 * cumpleaños en un año concreto. Es el mismo número que el de la ficha salvo en
 * un caso, y ese caso es la razón de que la regla exista.
 */

/**
 * El día del mes en que se celebra este cumpleaños en `year`.
 *
 * Quien nació un 29 de febrero lo celebra el 28 en los años que no son
 * bisiestos. Sin esto, tres de cada cuatro años no le tocaría nunca: ni «hoy»,
 * ni en la lista de febrero, porque su día no existe.
 */
export const celebrationDay = (birthday: Pick<Birthday, 'month' | 'day'>, year: number): number =>
  Math.min(birthday.day, CalendarDate.daysInMonth(year, birthday.month));

/**
 * Si una fecha de nacimiento se celebra `date`. Sirve igual para una fila de
 * Kn·Blog que para la fecha completa de la ficha: el año no cuenta.
 */
export const celebratesOn = (
  birth: Pick<Birthday, 'month' | 'day'>,
  date: CalendarDate,
): boolean => birth.month === date.month && celebrationDay(birth, date.year) === date.day;

const byName = (a: Birthday, b: Birthday): number =>
  a.displayName.localeCompare(b.displayName, 'es');

/** Los que cumplen `date`, por nombre. */
export const birthdaysOn = (
  birthdays: readonly Birthday[],
  date: CalendarDate,
): readonly Birthday[] => birthdays.filter((b) => celebratesOn(b, date)).sort(byName);

/** Los de un mes de un año, por el día en que se celebran y luego por nombre. */
export const birthdaysInMonth = (
  birthdays: readonly Birthday[],
  year: number,
  month: number,
): readonly Birthday[] =>
  birthdays
    .filter((b) => b.month === month)
    .sort((a, b) => celebrationDay(a, year) - celebrationDay(b, year) || byName(a, b));

/** La fecha en que `birthday` se celebra en `year`. */
export const celebrationDate = (birthday: Birthday, year: number): CalendarDate => {
  const date = CalendarDate.of(year, birthday.month, celebrationDay(birthday, year));
  /* `celebrationDay` ya acotó el día al mes, así que no puede fallar; si algún día
     falla, el cumpleaños se trata como el primero del mes en vez de reventar. */
  return date.ok ? date.value : CalendarDate.fromISOOrThrow(`${String(year)}-01-01`);
};

/**
 * El siguiente día en que alguien cumple, contando desde **mañana**.
 *
 * Para cuando hoy no cumple nadie: «el próximo es Paulo, el 3 de octubre» dice
 * más que una pantalla vacía. Da la vuelta al año —en diciembre, el siguiente
 * puede ser en enero— y devuelve a todos los de ese día. `null` sólo si no hay
 * un solo cumpleaños registrado.
 */
export const nextBirthdays = (
  birthdays: readonly Birthday[],
  today: CalendarDate,
): { readonly date: CalendarDate; readonly people: readonly Birthday[] } | null => {
  if (birthdays.length === 0) return null;

  const upcoming = (b: Birthday): CalendarDate => {
    const thisYear = celebrationDate(b, today.year);
    return thisYear.compare(today) > 0 ? thisYear : celebrationDate(b, today.year + 1);
  };

  let soonest: CalendarDate | null = null;
  for (const b of birthdays) {
    const date = upcoming(b);
    if (soonest === null || date.compare(soonest) < 0) soonest = date;
  }
  if (soonest === null) return null;
  const date = soonest;
  return { date, people: birthdaysOn(birthdays, date) };
};
